import { Building2, Gift, Search, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  downloadMembersCsv,
  useAdminMembers,
  useAdminMemberUpdate,
  useSetListservAdded,
  useSetMemberListed,
  type ListservState,
} from "@/features/admin/admin";
import {
  useApproveApplication,
  useDeclineApplication,
} from "@/features/applications/hooks/useApplications";
import type { AdminMemberRow } from "@/features/members/members.types";
import { DeclineDialog } from "@/components/DeclineDialog";
import { ListservCompare } from "@/components/admin/ListservCompare";
import { MemberCard } from "@/components/MemberCard";
import { RowListSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Badge } from "@/components/ui/badge";
import { SortableHeader, type SortState } from "@/components/SortableHeader";
import { Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const LISTSERV_MEMBERS_URL = "https://groups.google.com/g/AFELL/members";

/**
 * Members, in the shape of the Legacies page: what is waiting for a decision
 * first, then a table of everyone.
 *
 * The rule worth pinning: the tier and the payment state are *shown*, never
 * chosen — approval grants what the application asked for, paying grants
 * paid, a lapse takes it away. Only the role is a control. The application's
 * answers are not shown here because they are the applicant's profile, which
 * the name opens; what the row adds is the legacies filed with it, marked
 * New, since approving the application approves them too.
 */
export default function MembersAdminPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSortState] = useState<SortState>({ sort: null, direction: "asc" });
  // Changing the order changes which rows are on page one, so going back to it
  // is the only honest thing to do.
  const setSort = (next: SortState) => {
    setSortState(next);
    setPage(1);
  };
  const filter = useDebounced(search, 200);
  useEffect(() => setPage(1), [filter]);
  const { data, isPending, error } = useAdminMembers(
    filter,
    page,
    sort.sort,
    sort.direction
  );
  const update = useAdminMemberUpdate();
  const listserv = useSetListservAdded();
  const [exporting, setExporting] = useState(false);
  const listing = useSetMemberListed();
  const approve = useApproveApplication();
  const decline = useDeclineApplication();
  const busy = update.isPending || approve.isPending || decline.isPending;
  const failure = error || update.error || approve.error || decline.error;
  const [declining, setDeclining] = useState<AdminMemberRow | null>(null);

  const waiting =
    data?.pending ?? data?.members.filter((m) => m.application?.status === "pending") ?? [];
  // Only this page of members: a declined application is a thing you go
  // looking for, and the search box above the table is how you find one that
  // is not on the page in front of you.
  const declined =
    data?.members.filter((m) => m.application?.status === "declined") ?? [];
  const [showDeclined, setShowDeclined] = useState(false);
  const total = data?.total ?? 0;
  const pages = data ? Math.max(1, Math.ceil(total / data.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl">Members</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Applications wait at the top; everyone is in the table below. The tier
          follows from approval and from paying. Only the role is yours to set.
        </p>
      </div>
      {failure && <p className="text-destructive text-sm">{failure.message}</p>}

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>
            Waiting for review{waiting.length > 0 ? ` (${waiting.length})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isPending && !data ? (
            <RowListSkeleton />
          ) : waiting.length === 0 ? (
            <p className="text-muted-foreground text-sm">No applications waiting.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {waiting.map((member, index) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  index={index}
                  markNew
                  footer={
                    <ReviewFooter
                      member={member}
                      busy={busy}
                      onApprove={(waive) =>
                        member.application &&
                        approve.mutate({ id: member.application.id, waive })
                      }
                      onDecline={() => setDeclining(member)}
                    />
                  }
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Somewhere for a decision that has already been made. Declined
          applications were not on this page at all, so an admin asked "did I
          decline that, or lose the click?" and had nowhere to look. Todd,
          after the 1 September demo: "on members, need to have a spot (maybe
          can be hidden/collapsed) to show Declined applications." Collapsed,
          because it is a thing you go looking for rather than work waiting. */}
      {declined.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className={sectionTitleClass}>
              <button
                type="button"
                aria-expanded={showDeclined}
                className="underline-offset-4 hover:underline"
                onClick={() => setShowDeclined((wasOpen) => !wasOpen)}
              >
                Declined ({declined.length})
              </button>
            </CardTitle>
          </CardHeader>
          {showDeclined && (
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                {declined.map((member, index) => (
                  <MemberCard key={member.id} member={member} index={index} />
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>All members</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
              <Input
                aria-label="Search members"
                placeholder="Search members"
                className="pl-8"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {data && filter && (
              <p className="text-muted-foreground text-sm">
                {total} of {data.unfilteredTotal} people match
              </p>
            )}
            {/* Soft Network's mailing list is on Squarespace and stopped being
                fed when membership moved here, so this hands over the current
                one. It exports what the table is showing, filter and all. */}
            <Button
              variant="outline"
              size="sm"
              className="sm:ml-auto"
              disabled={exporting}
              onClick={async () => {
                setExporting(true);
                try {
                  await downloadMembersCsv(filter);
                } catch (e) {
                  toast.error((e as Error).message);
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? "Preparing…" : "Export CSV"}
            </Button>
          </div>

          {isPending && !data ? (
            <RowListSkeleton />
          ) : (
            // A floor under the fixed layout. The sized columns add up to 45rem,
            // so on a phone `w-full` squeezed Name, the only unsized one, to
            // nothing (Chelsea, 16 September: "I can't see the names"). With
            // the floor the table scrolls sideways instead.
            <Table className="w-full min-w-[51rem] table-fixed text-sm">
              <TableHeader>
                <TableRow>
                  <SortableHeader column="name" state={sort} onSort={setSort}>
                    Name
                  </SortableHeader>
                  <SortableHeader column="tier" state={sort} onSort={setSort} className="w-28">
                    Tier
                  </SortableHeader>
                  <SortableHeader column="payment" state={sort} onSort={setSort} className="w-24">
                    Payment
                  </SortableHeader>
                  <SortableHeader column="joined" state={sort} onSort={setSort} className="w-24">
                    Joined
                  </SortableHeader>
                  <SortableHeader column="listed" state={sort} onSort={setSort} className="w-24">
                    Directory
                  </SortableHeader>
                  <SortableHeader column="listserv" state={sort} onSort={setSort} className="w-44">
                    Listserv
                  </SortableHeader>
                  <TableHead className="w-10">
                    <span className="sr-only">Manage</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.members ?? []).map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="truncate" title={member.email}>
                      <Link
                        to={`/directory/${member.id}`}
                        className="underline-offset-4 hover:underline"
                      >
                        {member.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <TierBadge member={member} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <PaymentCell member={member} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : "not yet"}
                    </TableCell>
                    <TableCell>
                      <ListedCell
                        member={member}
                        onToggle={(listed, onError) =>
                          listing.mutate({ id: member.id, listed }, { onError })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <ListservCell
                        member={member}
                        busy={listserv.isPending}
                        onSet={(state) => listserv.mutate({ id: member.id, state })}
                      />
                    </TableCell>
                    <TableCell>
                      <Button asChild variant="ghost" size="icon-sm">
                        <Link
                          to={`/admin/members/${member.id}`}
                          aria-label={`Manage ${member.name}'s membership`}
                          title="Manage membership"
                        >
                          <Settings />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {pages > 1 && (
            <Pager page={page} pages={pages} total={total} noun="people" onPage={setPage} />
          )}
        </CardContent>
      </Card>

      <ListservCompare />

      <DeclineDialog
        open={declining !== null}
        onOpenChange={(open) => !open && setDeclining(null)}
        title={declining ? `Decline ${declining.name}'s application?` : "Decline"}
        description="They are emailed this note. Their account and profile stay as they are."
        busy={busy}
        onConfirm={(note) => {
          const app = declining?.application;
          setDeclining(null);
          if (app) decline.mutate({ id: app.id, note });
        }}
      />
    </div>
  );
}

/** The tier as a fact, in plain text. Paid says only Paid; the renewal date is
 *  a tooltip. Badges here made a column of pills out of something that is just
 *  a word, and drew the eye away from the things that are actually actionable. */
/**
 * The decision on one waiting application.
 *
 * A fee waiver is a request, and the reviewer's answer to it is not their
 * answer to the application: unchecking the box approves the membership and
 * leaves the fee standing, which beats declining somebody over a request they
 * made in good faith. The box is pre-filled from what they asked for, so
 * approving without reading it does what the applicant expects.
 *
 * Where a card is already saved there is nothing to waive — the reviewer sees
 * that instead, because offering a fee waiver for somebody who has already
 * agreed to pay is how a waiver gets granted by accident.
 */
function ReviewFooter({
  member,
  busy,
  onApprove,
  onDecline,
}: {
  member: AdminMemberRow;
  busy: boolean;
  onApprove: (waive: boolean | undefined) => void;
  onDecline: () => void;
}) {
  const application = member.application;
  const asked = Boolean(application?.waiver_requested);
  const [waive, setWaive] = useState(asked);

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs">
        {application &&
          `Applied ${new Date(application.submitted_at).toLocaleDateString()}`}
      </p>

      {asked && (
        <div className="space-y-1 rounded-md border px-2.5 py-2">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={waive}
              onChange={(e) => setWaive(e.target.checked)}
              disabled={busy}
            />
            <span>
              Waive the membership fee
              <span className="text-muted-foreground block text-xs">
                They asked for one. Unchecking approves the membership with the
                fee still due.
              </span>
            </span>
          </label>
          {application?.waiver_reason && (
            <p className="text-muted-foreground text-xs">
              &ldquo;{application.waiver_reason}&rdquo;
              {application.waiver_organization
                ? ` (${application.waiver_organization})`
                : ""}
            </p>
          )}
        </div>
      )}

      <ApplicationAnswers application={application} />

      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={busy}
          // Only when they asked: an approval that says nothing about the
          // waiver means "as asked", which is what the server defaults to.
          onClick={() => onApprove(asked ? waive : undefined)}
        >
          Approve{asked && waive ? " (waive fee)" : ""}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={onDecline}>
          Decline
        </Button>
      </div>
    </div>
  );
}

/** Field keys read back as English: `directory_email` → "Directory email". */
function humanize(key: string) {
  const words = key.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What they actually wrote, on the card that asks you to decide.
 *
 * The answers were already on the row — the server sends them for pending
 * applications precisely so a reviewer can read them — and nothing rendered
 * them, so approving or declining meant deciding on a name and a date. Todd,
 * after the 1 September demo: "I assume people will want to read the details
 * before taking approve/decline action."
 *
 * Collapsed by default. Two of these sit side by side on the queue, and the
 * entity description alone runs to 250 words.
 */
function ApplicationAnswers({ application }: { application: AdminMemberRow["application"] }) {
  const [open, setOpen] = useState(false);
  const answers = application?.answers;
  const rows = Object.entries(answers ?? {}).filter(([, value]) =>
    Array.isArray(value) ? value.length > 0 : String(value ?? "").trim() !== ""
  );
  if (rows.length === 0) return null;

  return (
    <div>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {open ? "Hide details" : `View details (${rows.length})`}
      </Button>
      {open && (
        <dl className="mt-2 max-h-64 space-y-2 overflow-y-auto rounded-md border px-2.5 py-2 text-xs">
          {rows.map(([key, value]) => (
            <div key={key}>
              <dt className="text-muted-foreground">{humanize(key)}</dt>
              <dd className="whitespace-pre-wrap">
                {Array.isArray(value) ? value.join(", ") : value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function TierBadge({ member }: { member: AdminMemberRow }) {
  if (member.tier === "paid") {
    const r = member.payment.renews_at
      ? new Date(member.payment.renews_at).toLocaleDateString()
      : null;
    return (
      <span
        title={r ? (member.payment.cancel_at_period_end ? `Ends ${r}` : `Renews ${r}`) : undefined}
      >
        Paid
      </span>
    );
  }
  if (member.tier === "gift") {
    // Covered by their organization's paid membership: the organization's
    // icon, the one the nav uses, not a gift somebody gave (June, 2026-09-18).
    const viaOrganization = member.payment.gift_source === "organization";
    const Icon = viaOrganization ? Building2 : Gift;
    const why = viaOrganization
      ? "Via organization"
      : member.payment.gift_source === "sponsorship"
        ? "Sponsored"
        : "Gift";
    return (
      <span className="inline-flex items-center gap-1">
        <Icon className="size-3 shrink-0" aria-hidden /> {why}
      </span>
    );
  }
  if (member.tier === "preview") {
    // Not a membership: an account with a profile and no approved application
    // behind it. Muted, because it is the absence of a decision rather than a
    // state anybody chose.
    return <span className="text-muted-foreground">Preview</span>;
  }
  return <span>Free</span>;
}

/** Whether they appear in the directory: the administrator's half of it, for
 *  somebody who has left their organization or asked to come off the list.
 *  Nothing else changes, so switching it back is a complete undo. The member's
 *  own "hide me" is theirs and is only shown here, never overridden.
 *
 *  Shows the choice at once rather than after the refetch: the notification
 *  switch on Settings did not, and was turned off without anyone seeing it. */
export function ListedCell({
  member,
  onToggle,
}: {
  member: AdminMemberRow;
  onToggle: (listed: boolean, onError: () => void) => void;
}) {
  const [chosen, setChosen] = useState<boolean | null>(null);
  useEffect(() => setChosen(null), [member.listed]);
  const listed = chosen ?? member.listed;
  // Listing somebody never approved would be a way round the application.
  const approvable = member.tier !== "preview";
  return (
    <div className="flex flex-col items-start gap-1">
      <Switch
        checked={listed}
        disabled={!listed && !approvable}
        aria-label={`List ${member.name} in the directory`}
        title={!listed && !approvable ? "Approve their application to list them" : undefined}
        onCheckedChange={(next) => {
          setChosen(next);
          onToggle(next, () => setChosen(null));
        }}
      />
      {listed && member.hidden && (
        <span className="text-muted-foreground text-xs">hidden by them</span>
      )}
    </div>
  );
}

/** The Google Group is maintained by hand — it has no API — so the site's part
 *  is to hand over the right address and remember what somebody did.
 *
 *  Three states: on it, opted out, or nobody has got to them yet. Only the
 *  last is work to do, which is what the roster command lists (Sami,
 *  2026-09-22: people who asked not to be on it were being tried again). */
export function ListservCell({
  member,
  busy,
  onSet,
}: {
  member: AdminMemberRow;
  busy: boolean;
  onSet: (state: ListservState) => void;
}) {
  const address = member.listserv_target || member.email;
  const added = Boolean(member.listserv_added_at);
  const optedOut = Boolean(member.listserv_opted_out_at);
  return (
    <div className="space-y-1">
      <button
        type="button"
        disabled={busy}
        onClick={() => navigator.clipboard?.writeText(address)}
        title={`Copy ${address}`}
        className="text-muted-foreground block max-w-full truncate text-xs underline-offset-4 hover:underline"
      >
        {address}
      </button>
      {added && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onSet("outstanding")}
          className="text-muted-foreground text-xs underline-offset-4 hover:underline"
          title={`Added ${new Date(member.listserv_added_at!).toLocaleDateString()}. Click to undo`}
        >
          <Badge variant="secondary" className="text-xs">On the listserv</Badge>
        </button>
      )}
      {optedOut && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onSet("outstanding")}
          className="text-muted-foreground text-xs underline-offset-4 hover:underline"
          title={`Opted out ${new Date(member.listserv_opted_out_at!).toLocaleDateString()}. Click to undo`}
        >
          <Badge variant="outline" className="text-xs">Opted out</Badge>
        </button>
      )}
      {!added && !optedOut && (
        <div className="flex items-center gap-1.5">
          <Button asChild size="xs" variant="outline">
            <a href={LISTSERV_MEMBERS_URL} target="_blank" rel="noreferrer">
              Add
            </a>
          </Button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onSet("added")}
            className="text-muted-foreground text-xs underline-offset-4 hover:underline"
          >
            mark done
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onSet("opted_out")}
            className="text-muted-foreground text-xs underline-offset-4 hover:underline"
            title="They asked not to be on the listserv"
          >
            opted out
          </button>
        </div>
      )}
    </div>
  );
}

export function PaymentCell({ member }: { member: AdminMemberRow }) {
  // The one state on this page that is a problem rather than a fact. Amber
  // rather than the destructive red: a lapsed membership is a thing to follow
  // up, not an error, and red beside 470 rows reads as alarm fatigue.
  if (member.status === "lapsed")
    return <span className="font-medium text-amber-600 dark:text-amber-500">Lapsed</span>;
  if (member.status === "pending_payment") return <>Awaiting</>;
  if (member.payment.is_paying) return <>{member.payment.cancel_at_period_end ? "Ending" : "Paying"}</>;
  return <>none</>;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
